import React, { useEffect, useState } from 'react';
import './ConfirmModal.css';

export interface ConfirmModalProps {
  variant: 'warning' | 'success' | 'error';
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  detail?: React.ReactNode;
  confirmLabel?: string;
  loading?: boolean;
  onConfirm?: () => Promise<void> | void;
  onClose: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  variant, icon, title, subtitle, detail,
  confirmLabel, loading = false, onConfirm, onClose,
}) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleConfirm = async () => {
    if (!onConfirm) { onClose(); return; }
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  const isConfirmMode = !!confirmLabel;

  return (
    <div className="cm-modal-bg" onClick={onClose}>
      <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`cm-modal-head cm-modal-head-${variant}`}>
          <div className="cm-modal-icon">{icon}</div>
          <div>
            <h3 className="cm-modal-title">{title}</h3>
            {subtitle && <p className="cm-modal-sub">{subtitle}</p>}
          </div>
        </div>

        {detail && (
          <div className="cm-modal-body">
            <div className="cm-modal-detail">{detail}</div>
          </div>
        )}

        <div className="cm-modal-foot">
          {isConfirmMode && (
            <button className="cm-btn cm-btn-ghost" onClick={onClose} disabled={busy || loading}>
              Cancel
            </button>
          )}
          <button
            className={`cm-btn cm-btn-${variant}`}
            onClick={handleConfirm}
            disabled={busy || loading}
          >
            {busy || loading ? 'Processing…' : (confirmLabel ?? 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
